import React, { useState, useEffect, useContext, useMemo, useCallback } from "react";
import { ErrorBoundary } from "../common/ErrorBoundary";
import { useFormContext } from "./FormContext";
import { stage4PreparePayload, Stage4TablePrep, stageLayoutStyles, stageFormStyles, StageNavButtons } from "./helper";
import { fetchDataAvailability } from "../../services/ApiWorkflow";
import Loader from "../common/Loader";
import { getCookie } from "../../services/DataRequestService";
import MenuContext from "./MenuContext";
import { openDatabase, upsertRecord, fetchRecordByKey, upgradeDatabaseInstance } from "../../services/IndexedDBUtil";
import { DATABASE_NAME, STORE_NAME } from "../../services/constants";

function StatusWithTooltip({ status, message }) {
  const [showTooltip, setShowTooltip] = useState(false);

  return (
    <div
      style={{
        position: "relative",
        display: "inline-block",
      }}
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      <span
        className={`text-[14px] px-3 py-1 rounded-full ${status === "Sufficient"
          ? "bg-green-100 text-green-800"
          : "bg-red-100 text-red-800"
          }`}
        style={{
          fontFamily: "Poppins, sans-serif",
          fontWeight: 600,
          cursor: "pointer",
        }}
      >
        {status}
      </span>

      {showTooltip && (
        <div
          style={{
            position: "absolute",
            bottom: "140%",
            left: "50%",
            transform: "translateX(-50%)",

            background: "#0f172a",
            color: "#fff",

            padding: "12px 14px",
            borderRadius: "10px",

            fontSize: "12px",
            lineHeight: "18px",

            minWidth: "320px",
            maxWidth: "420px",

            whiteSpace: "normal",
            wordBreak: "break-word",

            zIndex: 999999,

            boxShadow: "0 8px 24px rgba(0,0,0,0.25)",
          }}
        >
          {message}

          <div
            style={{
              position: "absolute",
              top: "100%",
              left: "50%",
              transform: "translateX(-50%)",

              width: 0,
              height: 0,

              borderLeft: "7px solid transparent",
              borderRight: "7px solid transparent",
              borderTop: "7px solid #0f172a",
            }}
          />
        </div>
      )}
    </div>
  );
}

export default function Stage4() {
  const { formData, setFormData, updateFormData } = useFormContext();
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [selectAllChecked, setSelectAllChecked] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const { setActiveTab } = useContext(MenuContext);

  const falseCondition =
    Array.isArray(formData?.stage3?.products) &&
    formData.stage3.products.length > 0 &&
    Array.isArray(formData?.stage3?.geography) &&
    formData.stage3.geography.length > 0;

  const buildId = (skuBreadcrumb, geoBreadcrumb) => {
    const sku = skuBreadcrumb.split("\\").pop();
    return `${sku}__${geoBreadcrumb}`;
  };

  const isCachedTableDataValid = (data) => {
    try {
      if (!Array.isArray(data)) return false;

      return data.every(
        (row) =>
          row &&
          typeof row === "object" &&
          typeof row.skuBreadcrumb === "string" &&
          row.skuBreadcrumb.includes("\\") &&
          row.sku &&
          typeof row.sku === "object" &&
          row.sku.sku_name &&
          row.geoBreadcrumb &&
          row.geography
      );
    } catch {
      return false;
    }
  };

  const isStage3Changed = useCallback(async () => {
    try {
      await openDatabase(DATABASE_NAME, 1, upgradeDatabaseInstance);
      const recordResult = await fetchRecordByKey(DATABASE_NAME, STORE_NAME, getCookie("sub") + "stage3Meta");
      
      let storedMeta = null;
      if (recordResult.success && recordResult.record && recordResult.record.data) {
        storedMeta = JSON.stringify(recordResult.record.data);
      }

      const currentMeta = JSON.stringify({
        products: formData?.stage3?.products,
        geography: formData?.stage3?.geography,
      });

      return storedMeta !== currentMeta;
    } catch {
      return true;
    }
  }, [formData?.stage3?.products, formData?.stage3?.geography]);

  const getThreshold = (data_frequency, forecast_frequency, total_rows) => {
    const baseMap = {
      Daily: { train: 90, test: 15 },
      Weekly: { train: 32, test: 12 },
      Monthly: { train: 18, test: 5 },
      Quarterly: { train: 8, test: 2 },
    };

    let train_threshold = 0;
    let test_threshold = 0;
    let target_points = 0;

    if (data_frequency === "Transaction") {
      const t = baseMap[forecast_frequency];
      if (t) {
        train_threshold = t.train;
        test_threshold = t.test;
        target_points = t.train + t.test;
      }
    } else {
      const t = baseMap[data_frequency];
      if (t) {
        train_threshold = t.train;
        test_threshold = t.test;
        target_points = t.train + t.test;
      }
    }

    const rows = Number(total_rows);

    const data_sufficiency_score =
      Number.isFinite(rows) && target_points > 0
        ? Math.round((rows / target_points) * 100)
        : 0;

    return { train_threshold, test_threshold, data_sufficiency_score };
  };

  const getDerivedStatus = (score) => {
    return score >= 100 ? "Sufficient" : "Insufficient";
  };

  const normalizeGeographyDisplay = (value) => {
    if (!value && value !== 0) return "";

    const parts = String(value)
      .split("\\")
      .map((part) => part.trim())
      .filter(Boolean);

    if (!parts.length) return "";

    const compacted = parts.filter((part, index) => part !== parts[index - 1]);
    const cleaned = compacted.length ? compacted : parts;

    return cleaned.length === 1 ? cleaned[0] : cleaned.join("\\");
  };

  const getFeatureSkuMatchers = () => {
    const selectedSkus = formData?.stage3?.featureBasedForecasting?.selected_skus || [];

    return {
      ids: new Set(selectedSkus.map((sku) => String(sku.sku_id ?? sku.skuId ?? sku.id ?? sku._rowId ?? "")).filter(Boolean)),
      names: new Set(selectedSkus.map((sku) => String(sku.sku_name ?? sku.skuName ?? sku.sku_description ?? sku.skuDescription ?? sku._description ?? sku.name ?? "").toLowerCase()).filter(Boolean)),
      codes: new Set(selectedSkus.map((sku) => String(sku.sku_code ?? sku.skuCode ?? sku._skuCode ?? sku.code ?? "").toLowerCase()).filter(Boolean)),
    };
  };

  const matchesFeatureSku = (sku, matchers) => {
    const skuId = String(sku?.sku_id ?? sku?.skuId ?? sku?.id ?? "").toLowerCase();
    const skuName = String(sku?.sku_name ?? sku?.skuName ?? sku?.description ?? sku?.skuDescription ?? "").toLowerCase();
    const skuCode = String(sku?.sku_code ?? sku?.skuCode ?? "").toLowerCase();

    return (
      (matchers.ids.size && matchers.ids.has(skuId)) ||
      (matchers.names.size && matchers.names.has(skuName)) ||
      (matchers.codes.size && matchers.codes.has(skuCode))
    );
  };

  const autoSelectFeatureCombinations = (data) => {
    const featureSelection = formData?.stage3?.featureBasedForecasting;

    if (!featureSelection?.selected_skus?.length || formData?.stage4?.featureSelectionApplied) {
      return;
    }

    const matchers = getFeatureSkuMatchers();
    // Rows are already SKU×geography pairs from the API; use the row's own
    // geography object ({level1..level5}) — submitTask expects that shape.
    const newCombos = data
      .filter((row) => matchesFeatureSku(row.sku, matchers))
      .map((row) => ({
        id: buildId(row.skuBreadcrumb, row.geoBreadcrumb),
        sku: row.sku,
        geography: row.geography,
      }));

    if (!newCombos.length) return;

    setFormData((prev) => {
      const existing = prev.stage4?.combinations || [];
      const merged = [...existing, ...newCombos].reduce((acc, item) => {
        if (!acc.some((combo) => combo.id === item.id)) acc.push(item);
        return acc;
      }, []);

      return {
        ...prev,
        stage4: {
          ...prev.stage4,
          combinations: merged,
          featureSelectedSkus: featureSelection.selected_skus,
          featureSelectionApplied: true,
        },
      };
    });
  };

  const getStatusReason = (row, threshold) => {
    const trainAvailable = Number(row?.train_data_available || 0);
    const testAvailable = Number(row?.test_data_available || 0);
    const trainGap = Math.max(0, threshold.train_threshold - trainAvailable);
    const testGap = Math.max(0, threshold.test_threshold - testAvailable);

    if (trainGap <= 0 && testGap <= 0) {
      return `This combination is sufficient because it meets both minimum data requirements: ${trainAvailable} training rows available against ${threshold.train_threshold} required, and ${testAvailable} test rows available against ${threshold.test_threshold} required.`;
    }

    const reasons = [];
    if (trainGap > 0) {
      reasons.push(
        `${trainGap} more training row${trainGap > 1 ? "s" : ""} needed`
      );
    }
    if (testGap > 0) {
      reasons.push(
        `${testGap} more test row${testGap > 1 ? "s" : ""} needed`
      );
    }

    return `This combination is insufficient because it does not meet the minimum data requirement. ${reasons.join(
      " and "
    )}. Currently it has ${trainAvailable}/${threshold.train_threshold} training rows and ${testAvailable}/${threshold.test_threshold} test rows.`;
  };

  // ── Feature-based forecasting mode ─────────────────────────────────────
  // UI shows one consolidated row per selected group; underneath, the SKU-level
  // stage4.combinations and every API payload stay exactly as in SKU mode.
  const isFeatureMode =
    formData?.stage3?.forecastingType === "feature" &&
    Boolean(formData?.stage3?.featureBasedForecasting?.selected_combinations?.length);

  const featureGroups = useMemo(() => {
    if (!isFeatureMode) return [];
    const groups = formData.stage3.featureBasedForecasting.selected_combinations;

    return groups
      .map((group) => {
        const codes = new Set(
          (group.skus || [])
            .map((s) => String(s.sku_code ?? s._skuCode ?? "").trim().toLowerCase())
            .filter(Boolean)
        );
        const names = new Set(
          (group.skus || [])
            .map((s) => String(s.sku_description ?? s._description ?? "").trim().toLowerCase())
            .filter(Boolean)
        );

        const rows = tableData.filter((row) => {
          const code = String(row?.sku?.sku_code ?? "").trim().toLowerCase();
          const name = String(row?.sku?.sku_name ?? "").trim().toLowerCase();
          if (code && codes.has(code)) return true;
          if (name && names.has(name)) return true;
          // Hierarchy SKU names are usually "CODE-Description"
          return [...codes].some((c) => c && name.startsWith(c));
        });

        // Best SKU row = the one with the most data available; its values are
        // shown as the group's consolidated thresholds/availability.
        const best = rows.reduce((acc, row) => {
          const total =
            Number(row?.train_data_available || 0) + Number(row?.test_data_available || 0);
          const accTotal = acc
            ? Number(acc.train_data_available || 0) + Number(acc.test_data_available || 0)
            : -1;
          return total > accTotal ? row : acc;
        }, null);

        return { group, rows, best };
      })
      .sort(
        (a, b) =>
          Number(String(a.group.groupLabel || "").replace(/\D/g, "") || 0) -
          Number(String(b.group.groupLabel || "").replace(/\D/g, "") || 0)
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFeatureMode, tableData, formData?.stage3?.featureBasedForecasting?.selected_combinations]);

  const getGroupStatusInfo = (entry) => {
    const total =
      Number(entry.best?.train_data_available || 0) +
      Number(entry.best?.test_data_available || 0);
    const threshold = getThreshold(
      formData?.stage2?.data_frequency,
      formData?.stage2?.forecast_frequency,
      total
    );
    return { threshold, status: getDerivedStatus(threshold.data_sufficiency_score) };
  };

  const filteredGroups = useMemo(() => {
    if (!isFeatureMode) return [];
    return featureGroups.filter((entry) => {
      const { status } = getGroupStatusInfo(entry);
      if (filter === "sufficient") return status === "Sufficient";
      if (filter === "insufficient") return status === "Insufficient";
      return true;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFeatureMode, featureGroups, filter, formData?.stage2?.data_frequency, formData?.stage2?.forecast_frequency]);

  // Keep repeated breadcrumb values clean (for example: India\India\India -> India),
  // but leave all other geography strings unchanged.
  const displayGeography = normalizeGeographyDisplay(
    formData?.stage3?.geography?.[0]?.breadcrumb || "India"
  );

  const filteredRows = useMemo(() => {
    return tableData.filter((row) => {
      const total_rows =
        Number(row?.train_data_available || 0) +
        Number(row?.test_data_available || 0);

      const { data_sufficiency_score } = getThreshold(
        formData?.stage2?.data_frequency,
        formData?.stage2?.forecast_frequency,
        total_rows
      );

      const status = getDerivedStatus(data_sufficiency_score);

      if (filter === "sufficient") return status === "Sufficient";
      if (filter === "insufficient") return status === "Insufficient";
      return true;
    });
  }, [tableData, filter, formData?.stage2?.data_frequency, formData?.stage2?.forecast_frequency]);

  const pageCount = useMemo(
    () =>
      Math.max(
        1,
        Math.ceil((isFeatureMode ? filteredGroups.length : filteredRows.length) / rowsPerPage)
      ),
    [isFeatureMode, filteredGroups.length, filteredRows.length, rowsPerPage]
  );

  const currentPageRows = useMemo(() => {
    const startIndex = (currentPage - 1) * rowsPerPage;
    return filteredRows.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredRows, currentPage, rowsPerPage]);

  const currentPageGroups = useMemo(() => {
    const startIndex = (currentPage - 1) * rowsPerPage;
    return filteredGroups.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredGroups, currentPage, rowsPerPage]);

  const filteredRowIds = useMemo(
    () => filteredRows.map((row) => buildId(row.skuBreadcrumb, row.geoBreadcrumb)),
    [filteredRows]
  );

  const currentPageIds = useMemo(
    () => currentPageRows.map((row) => buildId(row.skuBreadcrumb, row.geoBreadcrumb)),
    [currentPageRows]
  );

  const selectedIds = useMemo(
    () => new Set(formData?.stage4?.combinations?.map((c) => c.id) || []),
    [formData?.stage4?.combinations]
  );

  // Group-level selection helpers: a group is "selected" when every one of its
  // underlying SKU×geography combinations is present in stage4.combinations.
  const groupComboIds = (entry) =>
    entry.rows.map((row) => buildId(row.skuBreadcrumb, row.geoBreadcrumb));

  const isGroupSelected = (entry) =>
    entry.rows.length > 0 && groupComboIds(entry).every((id) => selectedIds.has(id));

  const setGroupCombos = (entries, checked) => {
    setFormData((prev) => {
      let updated = [...(prev.stage4?.combinations || [])];

      entries.forEach((entry) => {
        const ids = new Set(groupComboIds(entry));
        if (checked) {
          entry.rows.forEach((row) => {
            const id = buildId(row.skuBreadcrumb, row.geoBreadcrumb);
            if (!updated.some((c) => c.id === id)) {
              updated.push({ id, sku: row.sku, geography: row.geography });
            }
          });
        } else {
          updated = updated.filter((c) => !ids.has(c.id));
        }
      });

      return {
        ...prev,
        stage4: {
          ...prev.stage4,
          combinations: updated,
        },
      };
    });
  };

  const toggleGroupSelection = (entry) => {
    setGroupCombos([entry], !isGroupSelected(entry));
  };

  useEffect(() => {
    if (isFeatureMode) {
      const allSelected =
        filteredGroups.length > 0 && filteredGroups.every((entry) => isGroupSelected(entry));
      setSelectAllChecked(allSelected);
      return;
    }

    const allSelected =
      filteredRowIds.length > 0 &&
      filteredRowIds.every((id) => selectedIds.has(id));

    setSelectAllChecked(allSelected);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFeatureMode, filteredGroups, filteredRowIds, selectedIds]);

  useEffect(() => {
    if (currentPage > pageCount) {
      setCurrentPage(pageCount);
    }
  }, [currentPage, pageCount]);

  useEffect(() => {
    setCurrentPage(1);
  }, [filter, rowsPerPage, filteredRows.length, filteredGroups.length]);

  useEffect(() => {
    const init = async () => {
      try {
        setLoading(true);
        await openDatabase(DATABASE_NAME, 1, upgradeDatabaseInstance);
        const recordResult = await fetchRecordByKey(DATABASE_NAME, STORE_NAME, getCookie("sub") + "tableData");
        
        let parsedStoredData = null;
        let cacheValid = false;
        
        if (recordResult.success && recordResult.record && recordResult.record.data) {
          try {
            parsedStoredData = recordResult.record.data;
            cacheValid = isCachedTableDataValid(parsedStoredData);
          } catch {
            cacheValid = false;
          }
        }

        const stage3Changed = await isStage3Changed();
        const needRefresh = !parsedStoredData || !cacheValid || stage3Changed;

        if (needRefresh) {
          const payload = await stage4PreparePayload(formData);
          const result = await fetchDataAvailability(payload);

          if (result) {
            const data = await Stage4TablePrep(result);
            setTableData(data);
            autoSelectFeatureCombinations(data);

            try {
              await openDatabase(DATABASE_NAME, 1, upgradeDatabaseInstance);
              await upsertRecord(DATABASE_NAME, STORE_NAME, {
                id: getCookie("sub") + "tableData",
                data: data
              });

              await upsertRecord(DATABASE_NAME, STORE_NAME, {
                id: getCookie("sub") + "stage3Meta",
                data: {
                  products: formData?.stage3?.products,
                  geography: formData?.stage3?.geography,
                }
              });
            } catch (err) {
              console.warn("IndexedDB error, unable to save tableData or stage3Meta.", err);
            }
          }
        } else {
          setTableData(parsedStoredData);
          autoSelectFeatureCombinations(parsedStoredData);
        }
      } catch (error) {
        console.error("Error loading table data:", error);
      } finally {
        setLoading(false);
      }
    };

    if (falseCondition) init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [falseCondition, isStage3Changed]);

  const stats = isFeatureMode
    ? featureGroups.reduce(
        (acc, entry) => {
          const { status } = getGroupStatusInfo(entry);
          if (status === "Sufficient") acc.valid += 1;
          else acc.insufficient += 1;
          return acc;
        },
        { valid: 0, insufficient: 0 }
      )
    : tableData.reduce(
        (acc, row) => {
          const total_rows =
            Number(row?.train_data_available ?? 0) +
            Number(row?.test_data_available ?? 0);

          const { data_sufficiency_score } = getThreshold(
            formData?.stage2?.data_frequency,
            formData?.stage2?.forecast_frequency,
            total_rows
          );

          const status = getDerivedStatus(data_sufficiency_score);

          if (status === "Sufficient") acc.valid += 1;
          else acc.insufficient += 1;

          return acc;
        },
        { valid: 0, insufficient: 0 }
      );

  const totalCount = isFeatureMode ? featureGroups.length : tableData.length;
  const selectedCount = isFeatureMode
    ? featureGroups.filter((entry) => isGroupSelected(entry)).length
    : selectedIds.size;

  const handleCheckboxChange = (e) => {
    const { checked, dataset } = e.target;

    const id = dataset.id;
    const valueSku = JSON.parse(dataset.sku);
    const valueGeography = JSON.parse(dataset.geography);

    setFormData((prev) => {
      let updated = [...(prev.stage4?.combinations || [])];

      if (checked) {
        if (!updated.some((c) => c.id === id)) {
          updated.push({
            id,
            sku: valueSku,
            geography: valueGeography,
          });
        }
      } else {
        updated = updated.filter((c) => c.id !== id);
      }

      return {
        ...prev,
        stage4: {
          ...prev.stage4,
          combinations: updated,
        },
      };
    });
  };

  const toggleSelectAll = () => {
    if (isFeatureMode) {
      setGroupCombos(filteredGroups, !selectAllChecked);
      setSelectAllChecked(!selectAllChecked);
      return;
    }

    if (!selectAllChecked) {
      const newCombos = filteredRows.map((row) => ({
        id: buildId(row.skuBreadcrumb, row.geoBreadcrumb),
        sku: row.sku,
        geography: row.geography,
      }));

      const merged = [
        ...(formData?.stage4?.combinations || []),
        ...newCombos,
      ].reduce((acc, item) => {
        if (!acc.some((x) => x.id === item.id)) acc.push(item);
        return acc;
      }, []);

      updateFormData("stage4", {
        ...formData.stage4,
        combinations: merged,
      });
    } else {
      const remaining = (formData?.stage4?.combinations || []).filter(
        (combo) => !filteredRowIds.includes(combo.id)
      );

      updateFormData("stage4", {
        ...formData.stage4,
        combinations: remaining,
      });
    }

    setSelectAllChecked(!selectAllChecked);
  };

  const handlePageChange = (page) => {
    if (page < 1) return setCurrentPage(1);
    if (page > pageCount) return setCurrentPage(pageCount);
    setCurrentPage(page);
  };

  const getVisiblePages = (currentPage, totalPages) => {
    const pages = [];
    if (totalPages <= 5) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
      return pages;
    }

    const delta = 2;
    let start = Math.max(1, currentPage - delta);
    let end = Math.min(totalPages, currentPage + delta);

    if (currentPage - 1 <= delta) {
      start = 1;
      end = Math.min(5, totalPages);
    }

    if (totalPages - currentPage <= delta) {
      end = totalPages;
      start = Math.max(1, totalPages - 4);
    }

    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  };

  return (
    <ErrorBoundary>
      <div style={stageLayoutStyles.wrapper}>
        <div style={stageLayoutStyles.card}>
          <h2
            className="text-[16px] text-[#0c3c54] text-left mb-4"
            style={{ fontFamily: "Poppins, sans-serif", fontWeight: 600 }}
          >
            Step 4: Data Validation
          </h2>

          {/* STATS DASHBOARD */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8 mt-2">
            <div className="bg-white rounded-xl p-5 border border-[#e6eef4] border-l-4 border-l-[#032B4E]">
              <div className="text-[15px] sm:text-[16px] text-[#6a7d8c] font-semibold mb-2 tracking-wide" style={{ fontFamily: "Poppins, sans-serif" }}>
                TOTAL {isFeatureMode ? 'GROUPS' : 'COMBINATIONS'}
              </div>
              <div className="text-[28px] sm:text-[32px] font-bold leading-none text-[#0c3c54]" style={{ fontFamily: "Poppins, sans-serif" }}>
                {totalCount}
              </div>
            </div>

            <div className="bg-white rounded-xl p-5 border border-[#e6eef4] border-l-4 border-l-[#368E60]">
              <div className="text-[15px] sm:text-[16px] text-[#6a7d8c] font-semibold mb-2 tracking-wide" style={{ fontFamily: "Poppins, sans-serif" }}>
                SUFFICIENT
              </div>
              <div className="text-[28px] sm:text-[32px] font-bold leading-none text-[#368E60]" style={{ fontFamily: "Poppins, sans-serif" }}>
                {stats.valid}
              </div>
            </div>

            <div className="bg-white rounded-xl p-5 border border-[#e6eef4] border-l-4 border-l-[#D18A00]">
              <div className="text-[15px] sm:text-[16px] text-[#6a7d8c] font-semibold mb-2 tracking-wide" style={{ fontFamily: "Poppins, sans-serif" }}>
                INSUFFICIENT
              </div>
              <div className="text-[28px] sm:text-[32px] font-bold leading-none text-[#D18A00]" style={{ fontFamily: "Poppins, sans-serif" }}>
                {stats.insufficient}
              </div>
            </div>

            <div className="bg-white rounded-xl p-5 border border-[#e6eef4] border-l-4 border-l-[#E06639]">
              <div className="text-[15px] sm:text-[16px] text-[#6a7d8c] font-semibold mb-2 tracking-wide" style={{ fontFamily: "Poppins, sans-serif" }}>
                SELECTED
              </div>
              <div className="text-[28px] sm:text-[32px] font-bold leading-none text-[#E06639]" style={{ fontFamily: "Poppins, sans-serif" }}>
                {selectedCount}
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-4 mb-8 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex flex-wrap gap-3">
              {[
                { id: "all", label: "Show All", color: "#202b70", bg: "#E3F2F8" },
                {
                  id: "sufficient",
                  label: "Show Only Sufficient",
                  color: "#0E7C53",
                  bg: "#E3FCEF",
                },
                {
                  id: "insufficient",
                  label: "Show Only Insufficient",
                  color: "#D18A00",
                  bg: "#FFF8E1",
                },
              ].map((btn) => (
                <button
                  key={btn.id}
                  onClick={() => setFilter(btn.id)}
                  style={{
                    backgroundColor: filter === btn.id ? btn.color : btn.bg,
                    color: filter === btn.id ? "#ffffff" : btn.color,
                    boxShadow:
                      filter === btn.id
                        ? "0 4px 10px rgba(0,0,0,0.2)"
                        : "0 2px 6px rgba(0,0,0,0.1)",
                    border: `1px solid ${btn.color}`,
                    fontFamily: "Poppins, sans-serif",
                    fontSize: "14px",
                    fontWeight: 600,
                  }}
                  className="px-6 py-2 rounded-md transition-all duration-200"
                >
                  {btn.label}
                </button>
              ))}
            </div>

            <div className="flex flex-wrap gap-3 items-center">
              <div className="text-[14px] text-gray-600" style={{ fontFamily: "Poppins, sans-serif", fontWeight: 500 }}>
                {isFeatureMode
                  ? `Showing ${currentPageGroups.length} of ${filteredGroups.length} groups`
                  : `Showing ${currentPageRows.length} of ${filteredRows.length} rows`}
              </div>
              {/* A plain div, not <label> — a global `label{font-size:18px!important}`
                  reset in css.css hijacks any <label> on the page and can't be
                  overridden by a className, which is why this text kept
                  rendering at a different size than the "Showing..." div beside it. */}
              <div className="flex items-center gap-2 text-[14px] text-gray-600" style={{ fontFamily: "Poppins, sans-serif", fontWeight: 500 }}>
                <span>Rows per page:</span>
                <select
                  value={rowsPerPage}
                  onChange={(e) => setRowsPerPage(Number(e.target.value))}
                  className="border rounded-md px-2 py-1 text-[14px]"
                  style={{ minWidth: 80, fontFamily: "Poppins, sans-serif", color: "#4b5563", fontWeight: 500 }}
                >
                  {[10, 25, 50, 100].map((size) => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className="bg-white border-2 border-[#475C7E] rounded-3xl shadow-[0_8px_20px_rgba(0,0,0,0.08)] overflow-visible transition-all duration-300 hover:shadow-[0_12px_30px_rgba(0,0,0,0.12)] hover:-translate-y-1.5">
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <Loader size="large" />
              </div>
            ) : falseCondition && isFeatureMode ? (
              <table className="w-full text-left">
                <thead className="bg-[#032B4E] text-white sticky top-0 z-10">
                  <tr>
                    <th className="py-3 px-4 text-[14px] rounded-tl-3xl" style={{ fontFamily: "Poppins, sans-serif", fontWeight: 600 }}>Sl No</th>
                    <th className="py-3 px-4 text-[14px]" style={{ fontFamily: "Poppins, sans-serif", fontWeight: 600 }}>Group</th>
                    <th className="py-3 px-4 text-[14px]" style={{ fontFamily: "Poppins, sans-serif", fontWeight: 600 }}>Geography</th>
                    <th className="py-3 px-4 text-[14px]" style={{ fontFamily: "Poppins, sans-serif", fontWeight: 600 }}>Train Threshold</th>
                    <th className="py-3 px-4 text-[14px]" style={{ fontFamily: "Poppins, sans-serif", fontWeight: 600 }}>Train Data Available</th>
                    <th className="py-3 px-4 text-[14px]" style={{ fontFamily: "Poppins, sans-serif", fontWeight: 600 }}>Test Threshold</th>
                    <th className="py-3 px-4 text-[14px]" style={{ fontFamily: "Poppins, sans-serif", fontWeight: 600 }}>Test Data Available</th>
                    <th className="py-3 px-4 text-[14px]" style={{ fontFamily: "Poppins, sans-serif", fontWeight: 600 }}>Data Sufficiency Score</th>
                    <th className="py-3 px-4 text-center text-[14px] rounded-tr-3xl" style={{ fontFamily: "Poppins, sans-serif", fontWeight: 600 }}>
                      <input
                        type="checkbox"
                        checked={selectAllChecked}
                        onChange={toggleSelectAll}
                        className="accent-[#202b70] w-6 h-6 cursor-pointer"
                      />
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {currentPageGroups.map((entry, index) => {
                    const { threshold, status } = getGroupStatusInfo(entry);
                    const best = entry.best || {};
                    const reason = entry.best
                      ? getStatusReason(best, threshold)
                      : "No data availability information was returned for this group's SKUs.";

                    const isTrainMet = threshold.train_threshold <= Number(best.train_data_available || 0);
                    const isTestMet = threshold.test_threshold <= Number(best.test_data_available || 0);

                    return (
                      <tr
                        key={entry.group.combinationId}
                        className="border-b border-[#e5e7eb] hover:bg-[#f8faff] transition-all duration-200"
                      >
                        <td className="py-4 px-6 text-gray-900 text-[14px]" style={{ fontFamily: "Poppins, sans-serif", fontWeight: 600 }}>
                          {index + 1 + (currentPage - 1) * rowsPerPage}
                        </td>

                        <td className="py-4 px-6 text-gray-900 text-[14px]" style={{ fontFamily: "Poppins, sans-serif", fontWeight: 600 }}>
                          {entry.group.groupLabel || entry.group.combinationId}
                        </td>

                        <td className="py-4 px-6 text-gray-700 text-[14px]" style={{ fontFamily: "Poppins, sans-serif", fontWeight: 400 }}>
                          {displayGeography}
                        </td>

                        <td className="py-4 px-6 text-[14px]" style={{ fontFamily: "Poppins, sans-serif", fontWeight: 400 }}>
                          {threshold.train_threshold}
                        </td>

                        <td
                          className={`py-4 px-6 text-[14px] ${!isTrainMet ? "text-red-600" : "text-green-700"}`}
                          style={{ fontFamily: "Poppins, sans-serif", fontWeight: 400 }}
                        >
                          {best.train_data_available ?? "-"}
                        </td>

                        <td className="py-4 px-6 text-[14px]" style={{ fontFamily: "Poppins, sans-serif", fontWeight: 400 }}>
                          {threshold.test_threshold}
                        </td>

                        <td
                          className={`py-4 px-6 text-[14px] ${!isTestMet ? "text-red-600" : "text-green-700"}`}
                          style={{ fontFamily: "Poppins, sans-serif", fontWeight: 400 }}
                        >
                          {best.test_data_available ?? "-"}
                        </td>

                        <td className="py-4 px-6">
                          <StatusWithTooltip status={status} message={reason} />
                        </td>

                        <td className="py-4 px-6 text-center">
                          <input
                            type="checkbox"
                            checked={isGroupSelected(entry)}
                            onChange={() => toggleGroupSelection(entry)}
                            className="accent-[#202b70] w-5 h-5"
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            ) : falseCondition ? (
              <table className="w-full text-left">
                <thead className="bg-[#032B4E] text-white sticky top-0 z-10">
                  <tr>
                    <th className="py-3 px-4 text-[14px] rounded-tl-3xl" style={{ fontFamily: "Poppins, sans-serif", fontWeight: 600 }}>Sl No</th>
                    <th className="py-3 px-4 text-[14px]" style={{ fontFamily: "Poppins, sans-serif", fontWeight: 600 }}>SKU</th>
                    <th className="py-3 px-4 text-[14px]" style={{ fontFamily: "Poppins, sans-serif", fontWeight: 600 }}>Geography</th>
                    <th className="py-3 px-4 text-[14px]" style={{ fontFamily: "Poppins, sans-serif", fontWeight: 600 }}>Train Threshold</th>
                    <th className="py-3 px-4 text-[14px]" style={{ fontFamily: "Poppins, sans-serif", fontWeight: 600 }}>Train Data Available</th>
                    <th className="py-3 px-4 text-[14px]" style={{ fontFamily: "Poppins, sans-serif", fontWeight: 600 }}>Test Threshold</th>
                    <th className="py-3 px-4 text-[14px]" style={{ fontFamily: "Poppins, sans-serif", fontWeight: 600 }}>Test Data Available</th>
                    <th className="py-3 px-4 text-[14px]" style={{ fontFamily: "Poppins, sans-serif", fontWeight: 600 }}>Data Sufficiency Score</th>
                    <th className="py-3 px-4 text-center text-[14px] rounded-tr-3xl" style={{ fontFamily: "Poppins, sans-serif", fontWeight: 600 }}>
                      <input
                        type="checkbox"
                        checked={selectAllChecked}
                        onChange={toggleSelectAll}
                        className="accent-[#202b70] w-6 h-6 cursor-pointer"
                      />
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {currentPageRows.map((row, index) => {
                    const total_rows =
                      Number(row.train_data_available || 0) +
                      Number(row.test_data_available || 0);

                    const threshold = getThreshold(
                      formData.stage2.data_frequency,
                      formData.stage2.forecast_frequency,
                      total_rows
                    );

                    const status = getDerivedStatus(
                      threshold.data_sufficiency_score
                    );

                    const reason = getStatusReason(row, threshold);

                    const isTrainMet =
                      threshold.train_threshold <= row.train_data_available;
                    const isTestMet =
                      threshold.test_threshold <= row.test_data_available;

                    return (
                      <tr
                        key={`${row.skuBreadcrumb}-${row.geoBreadcrumb}`}
                        className="border-b border-[#e5e7eb] hover:bg-[#f8faff] transition-all duration-200"
                      >
                        <td
                          className="py-4 px-6 text-gray-900 text-[14px]"
                          style={{ fontFamily: "Poppins, sans-serif", fontWeight: 600 }}
                        >
                          {index + 1 + (currentPage - 1) * rowsPerPage}
                        </td>

                        <td
                          className="py-4 px-6 text-gray-900 text-[14px]"
                          style={{ fontFamily: "Poppins, sans-serif", fontWeight: 600 }}
                        >
                          {row?.sku?.sku_name || "-"}
                        </td>

                        <td
                          className="py-4 px-6 text-gray-700 text-[14px]"
                          style={{ fontFamily: "Poppins, sans-serif", fontWeight: 400 }}
                        >
                          {normalizeGeographyDisplay(row.geoBreadcrumb)}
                        </td>

                        <td
                          className="py-4 px-6 text-[14px]"
                          style={{ fontFamily: "Poppins, sans-serif", fontWeight: 400 }}
                        >
                          {threshold.train_threshold}
                        </td>

                        <td
                          className={`py-4 px-6 text-[14px] ${!isTrainMet ? "text-red-600" : "text-green-700"
                            }`}
                          style={{ fontFamily: "Poppins, sans-serif", fontWeight: 400 }}
                        >
                          {row.train_data_available}
                        </td>

                        <td
                          className="py-4 px-6 text-[14px]"
                          style={{ fontFamily: "Poppins, sans-serif", fontWeight: 400 }}
                        >
                          {threshold.test_threshold}
                        </td>

                        <td
                          className={`py-4 px-6 text-[14px] ${!isTestMet ? "text-red-600" : "text-green-700"
                            }`}
                          style={{ fontFamily: "Poppins, sans-serif", fontWeight: 400 }}
                        >
                          {row.test_data_available}
                        </td>

                        <td className="py-4 px-6">
                          <StatusWithTooltip
                            status={status}
                            message={reason}
                          />
                        </td>

                        <td className="py-4 px-6 text-center">
                          <input
                            type="checkbox"
                            data-id={buildId(row.skuBreadcrumb, row.geoBreadcrumb)}
                            data-sku={JSON.stringify(row.sku, null, 2)}
                            data-geography={JSON.stringify(row.geography, null, 2)}
                            checked={selectedIds.has(
                              buildId(row.skuBreadcrumb, row.geoBreadcrumb)
                            )}
                            onChange={handleCheckboxChange}
                            className="accent-[#202b70] w-5 h-5"
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            ) : (
              <div className="flex items-center justify-center py-20 text-gray-500">
                <div className="text-center">
                  <div className="text-[60px] mb-4">📊</div>
                  <p className="text-[20px] font-semibold" style={{ fontFamily: "Poppins, sans-serif" }}>No data available</p>
                  <p className="text-[14px] mt-2" style={{ fontFamily: "Poppins, sans-serif" }}>Please complete Stage 3 first</p>
                </div>
              </div>
            )}
          </div>

          {falseCondition && !loading && (isFeatureMode ? filteredGroups.length : filteredRows.length) > 0 && (
            <div className="flex justify-center mt-12" style={{ fontFamily: "Poppins, sans-serif" }}>
              <nav>
                <ul className="flex items-center space-x-4 text-[14px]">
                  <li>
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="px-6 py-3 border-2 border-[#1F4280] rounded-xl disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#E06639] hover:text-white transition-all duration-300 transform hover:scale-105 text-[14px]"
                      style={{ fontWeight: 600 }}
                    >
                      Previous
                    </button>
                  </li>

                  {getVisiblePages(currentPage, pageCount).map((pageNum) => (
                    <li key={pageNum}>
                      <button
                        onClick={() => handlePageChange(pageNum)}
                        className={`px-4 py-3 rounded-xl border-2 border-[#1F4280] transition-all duration-300 transform hover:scale-105 text-[14px] ${
                          currentPage === pageNum
                            ? "bg-[#E06639] text-white shadow-lg"
                            : "hover:bg-[#E06639] hover:text-white text-[#032B4E]"
                        }`}
                        style={{ fontWeight: 600 }}
                      >
                        {pageNum}
                      </button>
                    </li>
                  ))}

                  <li>
                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === pageCount}
                      className="px-6 py-3 border-2 border-[#1F4280] rounded-xl disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#E06639] hover:text-white transition-all duration-300 transform hover:scale-105 text-[14px]"
                      style={{ fontWeight: 600 }}
                    >
                      Next
                    </button>
                  </li>
                </ul>
              </nav>
            </div>
          )}

          <StageNavButtons
            onBack={() => setActiveTab("Stage3")}
            onNext={() => setActiveTab("Stage5")}
          />
        </div>
      </div>
    </ErrorBoundary>
  );
}